import { useCallback, useState } from 'react';

const STORAGE_KEY = 'plick:announcements:dismissed';

const readStorage = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const writeStorage = (set: Set<string>): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // localStorage puede fallar en private mode — ignoramos silenciosamente.
  }
};

interface UseDismissedAnnouncementsReturn {
  isDismissed: (uuid: string) => boolean;
  dismiss: (uuid: string) => void;
  reset: () => void;
}

export const useDismissedAnnouncements = (): UseDismissedAnnouncementsReturn => {
  const [dismissed, setDismissed] = useState<Set<string>>(() => readStorage());

  const dismiss = useCallback((uuid: string): void => {
    setDismissed((prev) => {
      if (prev.has(uuid)) return prev;
      const next = new Set(prev).add(uuid);
      writeStorage(next);
      return next;
    });
  }, []);

  const isDismissed = useCallback((uuid: string): boolean => dismissed.has(uuid), [dismissed]);

  const reset = useCallback((): void => {
    writeStorage(new Set());
    setDismissed(new Set());
  }, []);

  return { isDismissed, dismiss, reset };
};
